// src/pages/DemoHistory.jsx
import React, { useState, useEffect, useContext } from 'react';
import { Table, Input, DatePicker, Card, Tag, Typography, Select } from 'antd';
import { SearchOutlined, CalendarOutlined, FilterOutlined } from '@ant-design/icons';
import moment from 'moment';
import { UserContext } from "../UserContext";
import './DemoHistory.css';
import axiosInstance from "./axiosInstance";
import { toast } from "react-toastify";
import Footer from "../Footer";
import AdminNav from "../Navbars/AdminNav";
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const DemoHistory = () => {
    const [courses, setCourses] = useState([]);
    const { isAdmin, setUserRole, setUserInfo, userInfo } = useContext(UserContext);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [filters, setFilters] = useState({
        searchText: '',
        dateRange: [],
        course: null,
    });
    const navigate = useNavigate();

    useEffect(() => {
        fetchCourses();
        fetchDemoHistory();
    }, [pagination.current, pagination.pageSize, filters]);

    const fetchDemoHistory = () => {
        setLoading(true);
        const params = {
            search: filters.searchText,
            startDate: filters.dateRange[0]?.format('YYYY-MM-DD'),
            endDate: filters.dateRange[1]?.format('YYYY-MM-DD'),
            course: filters.course,
        };

        axiosInstance.get("/getDemoHistory", { params })
            .then((response) => {
                setData(response.data.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.count
                }));
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching demo history:", error);
                toast.error("Failed to load Demo history", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                setLoading(false);
            });
    };

    const fetchCourses = async () => {
        try {
            const response = await axiosInstance.get("/courses");
            setCourses(response.data);
        } catch (error) {
            console.error('Error fetching courses:', error);
            toast.error("Failed to load courses", {
                position: toast.POSITION.TOP_RIGHT,
            });
        }
    };

    const onLogout = () => {
        fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/logout`, {
            credentials: "include",
            method: "POST",
        })
            .then(() => {
                navigate("/");
            })
            .catch((error) => {
                console.error("Logout failed:", error);
            });
        setUserInfo(null);
        setUserRole("guest");
    };

    const handleSearch = (value) => {
        setFilters({ ...filters, searchText: value });
        setPagination({ ...pagination, current: 1 });
    };

    const handleDateChange = (dates) => {
        setFilters({ ...filters, dateRange: dates });
        setPagination({ ...pagination, current: 1 });
    };

    const handleCourseFilter = (value) => {
        setFilters({ ...filters, course: value });
        setPagination({ ...pagination, current: 1 });
    };

    const columns = [
        {
            title: 'Student Name',
            dataIndex: 'student_name',
            key: 'student_name',
            fixed: 'left',
            width: 150,
            sorter: (a, b) => a.student_name.localeCompare(b.student_name),
        },
        {
            title: 'Parent Name',
            dataIndex: 'parent_name',
            key: 'parent_name',
            width: 140,
        },
        {
            title: 'Age',
            dataIndex: 'age',
            key: 'age',
            width: 80,
            sorter: (a, b) => a.age - b.age,
        },
        {
            title: 'Number',
            dataIndex: 'number',
            key: 'number',
            width: 140,
        },
        {
            title: 'WhatsApp',
            dataIndex: 'whatsapp_number',
            key: 'whatsapp_number',
            width: 140,
            render: (text) => text || '-',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 200,
            render: (email) => (
                <span className="email-cell">
                    {email || '-'}
                </span>
            ),
        },
        {
            title: 'Country',
            dataIndex: 'country',
            key: 'country',
            width: 120,
        },
        {
            title: 'Time Zone',
            dataIndex: 'time',
            key: 'time',
            width: 140,
        },
        {
            title: 'Course',
            dataIndex: 'course',
            key: 'course',
            width: 180,
            render: (course) => (
                <Tag color="blue" className="course-tag">
                    {course || '-'}
                </Tag>
            ),
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: 120,
            render: (date) => moment(date).format('MMM D, YYYY'),
            sorter: (a, b) => new Date(a.date) - new Date(b.date),
        },
        {
            title: 'Time Slot',
            dataIndex: 'time_slot',
            key: 'time_slot',
            width: 120,
        },
        {
            title: 'Message',
            dataIndex: 'message',
            key: 'message',
            width: 200,
            render: (text) => (
                <span className="message-cell">
                    {text || '-'}
                </span>
            ),
        },
        {
            title: 'Final Date',
            dataIndex: 'final_date',
            key: 'final_date',
            width: 120,
            render: (text) => text ? moment(text).format('MMM D, YYYY') : '-',
            sorter: (a, b) => new Date(a.final_date) - new Date(b.final_date),
        },
        {
            title: 'Final Time',
            dataIndex: 'final_time',
            key: 'final_time',
            width: 120,
            render: (text) => text || '-',
        },
    ];

    const username = userInfo?.username;

    return (
        <>
            {username && isAdmin && <AdminNav username={username} onLogout={onLogout} />}
            <div className="demo-history-container">
                <div className="page-header">
                    <Title level={2} className="page-title">Demo Class History</Title>
                </div>
                
                <Card bordered={false} className="filter-card">
                    <div className="filter-grid">
                        <div className="search-container">
                            <div className="search-box">
                                <SearchOutlined className="search-icon" />
                                <Input
                                    placeholder="Search students by name, email or phone"
                                    allowClear
                                    size="large"
                                    onChange={(e) => handleSearch(e.target.value)}
                                    className="search-input"
                                />
                            </div>
                        </div>
                        
                        <div className="date-filter-container">
                            <div className="date-range-picker">
                                <CalendarOutlined className="date-icon" />
                                <RangePicker
                                    onChange={handleDateChange}
                                    className="date-picker"
                                    style={{ width: '100%' }}
                                    size="large"
                                    placeholder={['Start date', 'End date']}
                                />
                            </div>
                        </div>
                        
                        <div className="course-filter-container">
                            <Select
                                value={filters.course}
                                onChange={handleCourseFilter}
                                className="course-select"
                                placeholder={
                                    <span className="select-placeholder">
                                        <FilterOutlined style={{ marginRight: 8 }} />
                                        Select course
                                    </span>
                                }
                                allowClear
                                size="large"
                                style={{ width: '100%' }}
                                suffixIcon={null}
                            >
                                {courses.map((course) => (
                                    <Option key={course.Course_id} value={course.Course_id}>
                                        {course.coursename}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </div>
                </Card>

                <Card bordered={false} className="table-card">
                    <Table
                        columns={columns}
                        rowKey={(record) => `${record.id}-${record.date}`}
                        dataSource={data}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                        }}
                        loading={loading}
                        onChange={(newPagination) => setPagination(newPagination)}
                        scroll={{ x: 'max-content' }}
                        bordered
                        size="middle"
                        className="demo-history-table"
                    />
                </Card>
            </div>
            <Footer />
        </>
    );
};

export default DemoHistory;